/**
 * ThemeProvider — Detects system preference, persists user choice,
 * toggles dark/light via class on <html>.
 */

import type { ThemeState } from './AweniContext';
import { NativeService } from '../services/NativeService';

const STORAGE_KEY = 'aweni_theme';

export class ThemeProvider implements ThemeState {
  isDark: boolean;
  private _listeners: Array<(isDark: boolean) => void> = [];

  constructor() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      this.isDark = saved === 'dark';
    } else {
      this.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    this._apply();

    // Listen for system theme changes (only if no saved preference)
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (localStorage.getItem(STORAGE_KEY) === null) {
        this.isDark = e.matches;
        this._apply();
        this._notify();
      }
    });
  }

  toggle(): void {
    this.isDark = !this.isDark;
    localStorage.setItem(STORAGE_KEY, this.isDark ? 'dark' : 'light');
    this._apply();
    this._notify();
  }

  setTheme(dark: boolean): void {
    this.isDark = dark;
    localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
    this._apply();
    this._notify();
  }

  onChange(cb: (isDark: boolean) => void): void {
    this._listeners.push(cb);
  }

  private _apply(): void {
    const html = document.documentElement;
    if (this.isDark) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

    // Sync native status bar with theme
    NativeService.setStatusBarStyle(this.isDark ? 'dark' : 'light');
    NativeService.setStatusBarColor(this.isDark ? '#0F172A' : '#FFFFFF');
  }

  private _notify(): void {
    for (const cb of this._listeners) {
      cb(this.isDark);
    }
  }
}
